/**
 * Sign-out: clear BeHeard's session, then end Central's SSO session.
 *
 * ── THE CENTRAL CONTRACT (verified against Central's route source) ─────────────
 *
 * Central's RP-initiated logout is `GET /api/oidc/logout` (routes/api/oidc.ts)
 * with exactly two query parameters:
 *
 *   client_id                 'beheard'
 *   post_logout_redirect_uri  absolute URL; Central redirects there only when its
 *                             protocol+host match one of the client's registered
 *                             redirect URIs (utils/oidcRequestHygiene.ts
 *                             postLogoutRedirectAllowed), otherwise it answers
 *                             JSON `{ ok: true }` and does not redirect.
 *
 * It destroys the session named by Central's own `central_sid` cookie, so it is
 * a top-level NAVIGATION (the browser sends Central's first-party cookie), not a
 * fetch. There is no `id_token_hint`: nothing credential-like goes in the URL,
 * which matters because a URL ends up in access logs.
 *
 * BeHeard used to `fetch(…/api/oidc/logout, { method: 'POST' })`. Central has no
 * POST on that path, so the call 404'd, was swallowed, and the Central session
 * survived logout — the next "Sign in" went straight back in.
 *
 * KEEP IT A NAVIGATION. Central's handler only issues the 302 when the request
 * `Accept`s `text/html` (its `isBrowserNavigation` check); an XHR gets the JSON
 * branch instead, which looks like success while leaving the SSO cookie alive.
 * That is the same defect in a different disguise, so `navigate` must stay a
 * real `window.location` call.
 *
 * ── ORDER ──────────────────────────────────────────────────────────────────────
 *
 * Local state is cleared FIRST and unconditionally: whatever Central does (down,
 * slow, refusing the redirect), the access token is already gone from this tab.
 * Only then does the browser leave for Central.
 *
 * ── NO POST-LOGOUT TARGET, DELIBERATELY ───────────────────────────────────────
 *
 * This used to send `post_logout_redirect_uri = <origin>/login`, which made
 * signing out land on BeHeard's own `/login` — a page whose entire job is to
 * START A LOGIN (`pages/LoginPage.tsx` auto-calls `initiateLogin` from an
 * effect). So the sequence was: user signs out -> Central destroys the SSO
 * session -> Central redirects to /login -> /login immediately re-runs OIDC.
 * If anything had re-established the Central session in between, the user was
 * signed straight back in and logout looked like a no-op.
 *
 * Verified in Central's source (`backend/src/routes/api/oidc.ts`):
 * `GET /api/oidc/logout` destroys the session FIRST and unconditionally, and
 * with no `post_logout_redirect_uri` it lands a browser on Central's own
 * `/auth/login` — the canonical full-suite logout. Central's own comment names
 * the app-supplied target as what "put a second login interstitial in the
 * explicit-logout path". So the app supplies none.
 *
 * ── WHY THE URL IS BUILT HERE AND NOT BY THE SHARED HELPER ────────────────────
 *
 * `buildOidcLogoutUrl` from `@levelup/central-auth` cannot express this: the
 * INSTALLED build types `postLogoutRedirectUri` as a required `string` and
 * sets the parameter unconditionally, so there is no way to omit it. (Shared
 * `main` documents it as optional, but the installed dist lags that.) Building
 * two query parameters by hand is cheaper and clearer than depending on a
 * version of a helper this workspace does not have.
 */

export interface CentralLogoutInput {
  centralUrl: string;
  clientId: string;
}

/** The Central RP-initiated logout URL, or null when Central is not configured. */
export function centralLogoutUrl(input: CentralLogoutInput): string | null {
  if (!input.centralUrl) return null;
  const url = new URL('/api/oidc/logout', input.centralUrl);
  url.searchParams.set('client_id', input.clientId);
  // No `post_logout_redirect_uri`: Central owns the destination.
  return url.toString();
}

export interface LogoutDeps extends CentralLogoutInput {
  /** Drop the access token, cached API data and any in-flight OIDC state. */
  clearLocalSession: () => void;
  /** Top-level navigation (window.location.assign). */
  navigate: (url: string) => void;
}

/**
 * Clear locally, then hand the browser to Central's logout. Local clearing can
 * never be skipped by a Central problem, and a local problem never strands the
 * user signed in to Central.
 */
export function signOut(deps: LogoutDeps): void {
  let localError: unknown = null;
  try {
    deps.clearLocalSession();
  } catch (e) {
    localError = e;
  }

  let url: string | null = null;
  try {
    url = centralLogoutUrl(deps);
  } catch {
    url = null; // A malformed redirect URI must not keep the user on a half-cleared page.
  }
  if (url) deps.navigate(url);

  if (localError) throw localError;
}
